import { APPROVAL_QUEUE_ENDPOINTS } from "@/config/api";
import { httpClient } from "@/lib/http";
import type { ApiResponse } from "@/types/api";

import type {
    ApprovalDecisionPayload,
    ApprovalDecisionResult,
    ApprovalQueueDetail,
    ApprovalQueueFilters,
    ApprovalQueueListResponse,
    ApprovalQueuePagination,
} from "../types";

type ApprovalQueueListApiResponse = ApiResponse<ApprovalQueueListResponse> & {
    pagination?: ApprovalQueuePagination;
};

const buildQueryString = (filters: ApprovalQueueFilters) => {
    const params = new URLSearchParams();

    if (filters.page !== undefined) params.append("page", filters.page.toString());
    if (filters.size !== undefined) params.append("size", filters.size.toString());
    if (filters.status) params.append("status", filters.status);
    if (filters.actionType) params.append("actionType", filters.actionType);
    if (filters.targetType) params.append("targetType", filters.targetType);
    if (filters.mine !== undefined) params.append("mine", String(filters.mine));

    return params.toString();
};

export const getApprovalQueue = async (
    filters: ApprovalQueueFilters = {}
): Promise<ApprovalQueueListApiResponse> => {
    const queryString = buildQueryString(filters);
    const url = queryString
        ? `${APPROVAL_QUEUE_ENDPOINTS.LIST}?${queryString}`
        : APPROVAL_QUEUE_ENDPOINTS.LIST;

    const { data } = await httpClient.get<ApprovalQueueListApiResponse>(url);
    return data;
};

export const getApprovalQueueDetails = async (
    approvalId: string
): Promise<ApiResponse<ApprovalQueueDetail>> => {
    const { data } = await httpClient.get<ApiResponse<ApprovalQueueDetail>>(
        APPROVAL_QUEUE_ENDPOINTS.DETAILS(approvalId)
    );
    return data;
};

export const approveApprovalRequest = async (
    approvalId: string,
    payload?: ApprovalDecisionPayload
): Promise<ApiResponse<ApprovalDecisionResult>> => {
    const { data } = await httpClient.post<ApiResponse<ApprovalDecisionResult>>(
        APPROVAL_QUEUE_ENDPOINTS.APPROVE(approvalId),
        payload
    );
    return data;
};

export const rejectApprovalRequest = async (
    approvalId: string,
    payload?: ApprovalDecisionPayload
): Promise<ApiResponse<ApprovalDecisionResult>> => {
    const { data } = await httpClient.post<ApiResponse<ApprovalDecisionResult>>(
        APPROVAL_QUEUE_ENDPOINTS.REJECT(approvalId),
        payload
    );
    return data;
};

export const cancelApprovalRequest = async (
    approvalId: string,
    payload?: ApprovalDecisionPayload
): Promise<ApiResponse<ApprovalDecisionResult>> => {
    const { data } = await httpClient.post<ApiResponse<ApprovalDecisionResult>>(
        APPROVAL_QUEUE_ENDPOINTS.CANCEL(approvalId),
        payload
    );
    return data;
};
